/** AT05 copy (one group per control; a ticket edits only its own file). */
export const at05PtBr = {
  title: 'Revisão mensal de indícios de impairment',
  description:
    'Revisão da competência confirmando que os indícios de perda de valor (bens ociosos, depreciação parada no valor residual, projetos baixados, ativos de vida útil indefinida e intangíveis em desenvolvimento) foram avaliados. No mês do encerramento do exercício, ela também cobre a avaliação de impairment do fim do exercício e a revisão de vida útil, valor residual e método.',
  prerequisite: 'Requer a revisão mensal registrada por um responsável.',
  reviewRequired:
    'O sistema não calcula nem propõe perda por impairment: a avaliação dos indícios é sempre manual. A revisão cobre a competência inteira, não cada bem.',
  reviewState: {
    title: 'Revisão da competência',
    valid: 'Revisão registrada',
    nothingDue: 'Nenhum indício a avaliar nesta competência',
  },
  yearEndTask:
    'A tarefa anual de teste de ativos de vida útil indefinida é só um lembrete: ela não substitui a revisão mensal e não aprova este controle.',
  reasons: {
    IMPAIRMENT_REVIEW_MISSING: 'Competência sem revisão dos indícios de impairment',
    IMPAIRMENT_REVIEW_REJECTED: 'Revisão dos indícios de impairment rejeitada',
    IMPAIRMENT_REVIEW_INVALIDATED: 'Revisão dos indícios de impairment não vale mais',
    IMPAIRMENT_INDICATOR_IDLE_ASSET: 'Bem ocioso',
    IMPAIRMENT_INDICATOR_RESIDUAL_FLOOR: 'Depreciação parada no valor residual com o bem em uso',
    IMPAIRMENT_INDICATOR_PROJECT_WRITTEN_OFF: 'Projeto intangível baixado na competência',
    IMPAIRMENT_INDICATOR_INDEFINITE_LIFE: 'Bem de vida útil indefinida em uso',
    IMPAIRMENT_INDICATOR_INTANGIBLE_IN_DEVELOPMENT:
      'Intangível em desenvolvimento, ainda não disponível para uso',
    IMPAIRMENT_YEAR_END_ASSESSMENT_DUE: 'Avaliação de fim de exercício devida',
  },
  wayOut: {
    IMPAIRMENT_REVIEW_MISSING:
      'Avalie os indícios listados e registre a revisão desta competência. Sem revisão, o controle não é aprovado.',
    IMPAIRMENT_REVIEW_REJECTED:
      'A revisão foi rejeitada. Trate os indícios listados e registre uma nova revisão da competência.',
    IMPAIRMENT_REVIEW_INVALIDATED:
      'Os indícios ou a regra do controle mudaram depois da revisão. Reavalie os indícios listados e registre uma nova revisão.',
    IMPAIRMENT_INDICATOR_IDLE_ASSET:
      'Bem fora de uso aguardando baixa: registre a baixa (ver AT04) ou, se o bem voltou a ser usado, corrija o status. Inclua-o na revisão da competência.',
    IMPAIRMENT_INDICATOR_RESIDUAL_FLOOR:
      'O bem segue em uso sem depreciar: pode ser preciso revisar vida útil e valor residual (CPC 27 §51). Avalie também se há indício de perda de valor e inclua o bem na revisão da competência.',
    IMPAIRMENT_INDICATOR_PROJECT_WRITTEN_OFF:
      'Confirme que a baixa do projeto foi considerada na avaliação de indícios e inclua-o na revisão da competência.',
    IMPAIRMENT_INDICATOR_INDEFINITE_LIFE:
      'Ativos de vida útil indefinida são avaliados todo mês. Inclua o bem na revisão da competência.',
    IMPAIRMENT_INDICATOR_INTANGIBLE_IN_DEVELOPMENT:
      'Intangível ainda não disponível para uso não é amortizado: o teste de recuperabilidade é anual (CPC 01 §10) e esta revisão acompanha os indícios todo mês. Inclua o projeto na revisão da competência.',
    IMPAIRMENT_YEAR_END_ASSESSMENT_DUE:
      'Esta competência contém o fim do exercício. Registre a revisão cobrindo a avaliação de impairment do fim do exercício (CPC 01 §9) e a revisão da vida útil, do valor residual e do método (CPC 27 §51, CPC 04 §104).',
  },
  observations: {
    AT05_REGISTER_EMPTY:
      'Nenhum bem com regra neste livro e nenhum projeto intangível na entidade. Registre a revisão da competência para confirmar que não há ativos a avaliar.',
    AT05_REGISTER_EMPTY_REVIEWED: 'Registro sem bens confirmado pela revisão da competência',
    AT05_WRITE_OFF_UNDATED:
      'Projetos baixados sem data de baixa: não dá para saber em que competência avaliar. Informe a data de baixa.',
    AT05_IDLE_ASSETS: 'Bens ociosos cobertos pela revisão',
    AT05_RESIDUAL_FLOOR_ASSETS:
      'Bens com depreciação parada no valor residual cobertos pela revisão',
    AT05_WRITTEN_OFF_PROJECTS: 'Projetos baixados cobertos pela revisão',
    AT05_INDEFINITE_LIFE_ASSETS: 'Bens de vida útil indefinida cobertos pela revisão',
    AT05_INTANGIBLES_IN_DEVELOPMENT: 'Intangíveis em desenvolvimento cobertos pela revisão',
    AT05_YEAR_END_ASSESSMENT: 'Avaliação de fim de exercício coberta pela revisão',
  },
  fiscalYearEnd: 'Encerramento do exercício',
  asset: 'Bem {{code}}',
  project: 'Projeto {{code}}',
  openAsset: 'Abrir bem',
  openProject: 'Abrir projeto',
  listTruncated: 'Mostrando {{shown}} de {{total}} apontamentos.',
};

export const at05En: typeof at05PtBr = {
  title: 'Monthly impairment indicator review',
  description:
    'A review of the competência confirming that impairment indicators (idle assets, depreciation stopped at the residual floor, written-off projects, indefinite-life assets and intangibles under development) were assessed. In the competência that holds the fiscal year end it also covers the year-end impairment assessment and the review of useful life, residual value and method.',
  prerequisite: 'Needs a monthly review recorded by a reviewer.',
  reviewRequired:
    'The system does not calculate or propose an impairment loss: assessing the indicators is always manual. The review covers the whole competência, not each asset.',
  reviewState: {
    title: 'Competência review',
    valid: 'Review recorded',
    nothingDue: 'No indicator to assess in this competência',
  },
  yearEndTask:
    'The yearly indefinite-life asset test task is only a reminder: it does not replace the monthly review and does not approve this control.',
  reasons: {
    IMPAIRMENT_REVIEW_MISSING: 'Competência with no impairment indicator review',
    IMPAIRMENT_REVIEW_REJECTED: 'Impairment indicator review rejected',
    IMPAIRMENT_REVIEW_INVALIDATED: 'Impairment indicator review no longer applies',
    IMPAIRMENT_INDICATOR_IDLE_ASSET: 'Idle asset',
    IMPAIRMENT_INDICATOR_RESIDUAL_FLOOR:
      'Depreciation stopped at the residual floor while the asset is in use',
    IMPAIRMENT_INDICATOR_PROJECT_WRITTEN_OFF: 'Intangible project written off in the competência',
    IMPAIRMENT_INDICATOR_INDEFINITE_LIFE: 'Indefinite-life asset in use',
    IMPAIRMENT_INDICATOR_INTANGIBLE_IN_DEVELOPMENT:
      'Intangible under development, not yet available for use',
    IMPAIRMENT_YEAR_END_ASSESSMENT_DUE: 'Year-end assessment due',
  },
  wayOut: {
    IMPAIRMENT_REVIEW_MISSING:
      'Assess the listed indicators and record the review of this competência. Without a review the control is not approved.',
    IMPAIRMENT_REVIEW_REJECTED:
      'The review was rejected. Deal with the listed indicators and record a new review of the competência.',
    IMPAIRMENT_REVIEW_INVALIDATED:
      'The indicators or the control rule changed after the review. Reassess the listed indicators and record a new review.',
    IMPAIRMENT_INDICATOR_IDLE_ASSET:
      'Asset out of use awaiting disposal: record the disposal (see AT04) or, if the asset is back in use, correct its status. Include it in the review of the competência.',
    IMPAIRMENT_INDICATOR_RESIDUAL_FLOOR:
      'The asset stays in use without depreciating: its useful life and residual value may need to be reviewed (CPC 27 §51). Also assess whether there is an impairment indicator and include the asset in the review of the competência.',
    IMPAIRMENT_INDICATOR_PROJECT_WRITTEN_OFF:
      'Confirm the project write-off was considered when assessing indicators and include it in the review of the competência.',
    IMPAIRMENT_INDICATOR_INDEFINITE_LIFE:
      'Indefinite-life assets are assessed every month. Include the asset in the review of the competência.',
    IMPAIRMENT_INDICATOR_INTANGIBLE_IN_DEVELOPMENT:
      'An intangible not yet available for use is not amortised: its impairment test is annual (CPC 01 §10) and this review follows its indicators every month. Include the project in the review of the competência.',
    IMPAIRMENT_YEAR_END_ASSESSMENT_DUE:
      'This competência holds the fiscal year end. Record the review covering the year-end impairment assessment (CPC 01 §9) and the review of useful life, residual value and method (CPC 27 §51, CPC 04 §104).',
  },
  observations: {
    AT05_REGISTER_EMPTY:
      'No asset with a rule on this book and no intangible project in the entity. Record the review of the competência to confirm there are no assets to assess.',
    AT05_REGISTER_EMPTY_REVIEWED: 'Empty register confirmed by the review of the competência',
    AT05_WRITE_OFF_UNDATED:
      'Written-off projects with no write-off date: it is not possible to tell which competência to assess. Enter the write-off date.',
    AT05_IDLE_ASSETS: 'Idle assets covered by the review',
    AT05_RESIDUAL_FLOOR_ASSETS:
      'Assets with depreciation stopped at the residual floor covered by the review',
    AT05_WRITTEN_OFF_PROJECTS: 'Written-off projects covered by the review',
    AT05_INDEFINITE_LIFE_ASSETS: 'Indefinite-life assets covered by the review',
    AT05_INTANGIBLES_IN_DEVELOPMENT: 'Intangibles under development covered by the review',
    AT05_YEAR_END_ASSESSMENT: 'Year-end assessment covered by the review',
  },
  fiscalYearEnd: 'Fiscal year end',
  asset: 'Asset {{code}}',
  project: 'Project {{code}}',
  openAsset: 'Open asset',
  openProject: 'Open project',
  listTruncated: 'Showing {{shown}} of {{total}} findings.',
};
